<template>
  <div>
    <v-list-item v-for="linha in linhas" :key="linha.key" dense>
      <v-list-item-content class="py-0">
        <v-list-item-title>
          <span class="font-12 font-weight-medium" v-text="linha.text" />
        </v-list-item-title>

        <v-list-item-subtitle>
          <span class="font-12">{{ Helpers.formatarValorComCasas(linha.value.value, linha.casas) }}</span>
        </v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>

    <div class="ml-4 font-13 d-flex">
      <div>
        <p class="ma-0 font-weight-medium">Média de Compra por Cliente</p>
        <p class="ma-0 mt-n1">{{ Helpers.formatarValorComCasas(MEDIA_COMPUTED, 2) }}</p>
      </div>

      <v-divider vertical class="mx-2" />

      <div>
        <p class="ma-0 font-weight-medium">Influência (%)</p>
        <SysInputFormatadorDeValor
          v-model="influenciar"
          inteiro
          remover-valor-irrelevante
          centralizar
          class="input-regular font-resp w-82 py-0 h-20px"
          sufixo="%"
          @focus="ev => ev.target.select()"
          @blur="salvar"
          @keyup.enter="salvar"
        />
      </div>
    </div>

    <v-switch
      v-model="tipo"
      hide-details
      dense
      class="ml-3 my-3"
      @change="salvar"
    >
      <template #label>
        <span class="font-13 mt-n1 ml-n1">Não usar média e adicionar valor fixo</span>
      </template>
    </v-switch>

    <v-list-item dense>
      <v-list-item-content class="d-flex justify-space-between align-center py-0">
        <v-list-item-title>
          <span class="font-12 font-weight-medium" v-text="'Valor Fixo'" />
        </v-list-item-title>

        <TextFieldFormatadorDeValor
          v-model="valor_fixo"
          :maxDigitos="2"
          dense
          :disabled="!tipo"
          :rules="[v => parseFloat(v) > 0 || 'O valor deve ser maior que zero.']"
          :min="0"
          type="number"
          placeholder="Valor fixo"
          class="text-field-centralizado w-full font-12 w-75"
          append-outer-icon="mdi-content-save-move-outline"
          @focus="ev => ev.target.select()"
          @click:append-outer="salvar"
          @blur="salvar"
          @keyup.enter="salvar"
          ref="textFieldValorFixoAnalisePorLote"
          @input="v => emit('updated:valor-fixo', v)"
        />
      </v-list-item-content>
    </v-list-item>

    <v-list-item dense>
      <v-list-item-content class="d-flex justify-space-between align-center py-0">
        <v-list-item-title>
          <span class="font-12 font-weight-medium" v-text="'Cobertura Estoque (dias)'" />
        </v-list-item-title>

        <TextFieldFormatadorDeValor
          v-model="cobertura_estoque"
          :maxDigitos="0"
          dense
          :rules="[v => v > 0 || 'O valor deve ser maior que zero.']"
          :min="0"
          type="number"
          placeholder="Cobertura Estoque"
          class="text-field-centralizado font-12 w-75"
          append-outer-icon="mdi-content-save-move-outline"
          @focus="ev => ev.target.select()"
          @click:append-outer="salvar"
          @blur="salvar"
          @keyup.enter="salvar"
          ref="textFieldCoberturaEstoqueAnalisePorLote"
        />
      </v-list-item-content>
    </v-list-item>

    <v-list-item dense>
      <v-list-item-content class="d-flex justify-space-between align-center py-0">
        <v-list-item-title>
          <span class="font-12 font-weight-medium" v-text="'Ponta Estoque (%)'" />
        </v-list-item-title>

        <TextFieldFormatadorDeValor
          v-model="ponta_estoque_percentual"
          :rules="[v => v > 0 || 'O valor deve ser maior que zero.']"
          :min="0"
          :maxDigitos="0"
          dense
          type="number"
          placeholder="%"
          class="text-field-centralizado font-12 w-75"
          append-outer-icon="mdi-content-save-move-outline"
          @focus="ev => ev.target.select()"
          @click:append-outer="salvar"
          @blur="salvar"
          @keyup.enter="salvar"
          ref="textFieldPontaEstoquePercentualAnalisePorLote"
        />
      </v-list-item-content>
    </v-list-item>
  </div>
</template>

<script setup>
import api from "@/api/api.js";
import TextFieldFormatadorDeValor from '@/components/utils/TextFieldFormatadorDeValor.vue'
import { logout } from "@/helpers";
import { computed, onMounted, ref, watch } from 'vue'
import SysInputFormatadorDeValor from "@/components/utils/InputFormatadorDeValor.vue";

onMounted(carregarDados);

let timeoutId;
const total_clientes = ref(2);
const total_comprado = ref(650);
const media_compra = ref(320);
const influenciar = ref(0);
const cobertura_estoque = ref(0);
const ponta_estoque_percentual = ref(0);
const valor_fixo = ref(0);
const tipo = ref(false);
const emit = defineEmits(['updated:atualizar', 'updated:ponta-estoque-percentual', 'updated:valor-fixo']);
const props = defineProps({ produto: Object, atualizar_por_desconsiderados: Number });
const MEDIA_COMPUTED = computed(() => media_compra.value + ((media_compra.value * influenciar.value) / 100));
const PONTA_ESTOQUE_COMPUTED = computed(() => {
  return Number(tipo.value ? valor_fixo.value : MEDIA_COMPUTED.value) * (ponta_estoque_percentual.value / 100);
});
const PONTO_PEDIDO_COMPUTED = computed(() => !tipo.value ? (MEDIA_COMPUTED.value / 90) * cobertura_estoque.value : valor_fixo.value);
const COBERTURA_ESTOQUE_COMPUTED= computed(() => MEDIA_COMPUTED.value * cobertura_estoque.value / 30);
const textFieldValorFixoAnalisePorLote = ref(null);
const textFieldInfluenciarAnalisePorLote = ref(null);
const textFieldCoberturaEstoqueAnalisePorLote = ref(null);
const textFieldPontaEstoquePercentualAnalisePorLote = ref(null);

const linhas = [
  { key: 1, text: 'Total de Clientes', value: total_clientes, casas: 0 },
  { key: 2, text: 'Total Comprado no Período', value: total_comprado, casas: 2 },
  // { key: 3, text: 'Média de Compra por Cliente', value: MEDIA_COMPUTED, casas: 2 },
  // { key: 4, text: 'Ponto Pedido por Lote', value: PONTO_PEDIDO_COMPUTED, casas: 2 },
];

watch(influenciar, (valor) => {
  if (valor < (-100)) {
    influenciar.value = (-100);
    return;
  }

  if (valor > 999) {
    influenciar.value = 999;
    return;
  }

  if (isNaN(valor)) influenciar.value = 0;
});

watch(ponta_estoque_percentual, (valor) => {
  if (valor < 0) {
    ponta_estoque_percentual.value = (parseFloat(valor) * (- 1));
    return;
  }

  if (valor > 999) {
    ponta_estoque_percentual.value = 999;
    return;
  }

  if (isNaN(valor)) ponta_estoque_percentual.value = 0;
});

watch(() => props.atualizar_por_desconsiderados, carregarDados);
watch(() => props.produto, carregarDados);

function carregarDados() {
  const params = props.produto;
  api.get("/compras/produtos/analise-por-lotes/buscar-analise-por-cliente", {params})
    .then(response => {
      const { item } = response.data;
      media_compra.value = parseFloat(item.media_compra_cliente);
      total_comprado.value = parseFloat(item.total_comprado_periodo);
      total_clientes.value = item.total_clientes;
      tipo.value = item.tipo_ponto_pedido;
      valor_fixo.value = item.ponto_pedido_fixo;
      influenciar.value = item.ponto_pedido_percentual_influencia;
      cobertura_estoque.value = item.cobertura_estoque;
      ponta_estoque_percentual.value = item.ponta_estoque_percentual;
      emit('updated:ponta-estoque-percentual', PONTA_ESTOQUE_COMPUTED);
      emit('updated:ponto-pedido', PONTO_PEDIDO_COMPUTED);
      emit('updated:cobertura-estoque', COBERTURA_ESTOQUE_COMPUTED);

      clearTimeout(timeoutId);
    })
    .catch(logout);
}

function salvar() {

  const params = {
    produtos: [{ id_grupo: props.produto.id_grupo, id_produto: props.produto.idproduto }],
    tipo_ponto_pedido: tipo.value,
    ponto_pedido_fixo: valor_fixo.value || 0,
    ponto_pedido_percentual_influencia: influenciar.value || 0,
    cobertura_estoque: cobertura_estoque.value || 60,
    ponta_estoque_percentual: ponta_estoque_percentual.value || 50,
  }

  api.post("/compras/produtos/analise-por-lotes/salvar-parametrizacao", params)
    .then(response => {
      carregarDados();
      emit('updated:atualizar');
    })
    .catch(logout)
    .finally(removerBlur);
}

function removerBlur() {
  if (textFieldValorFixoAnalisePorLote.value) textFieldValorFixoAnalisePorLote.value.$children[0].blur();
  if (textFieldInfluenciarAnalisePorLote.value) textFieldInfluenciarAnalisePorLote.value.$children[0].blur();
  if (textFieldCoberturaEstoqueAnalisePorLote.value) textFieldCoberturaEstoqueAnalisePorLote.value.$children[0].blur();
  if (textFieldPontaEstoquePercentualAnalisePorLote.value) textFieldPontaEstoquePercentualAnalisePorLote.value.$children[0].blur();
}
</script>
